import { IconButton, Menu, Scrollbar, Text } from "@carbon6/c6-frontend-design";
import { useEffect, useRef, useState, type ReactNode } from "react";
import type { DatasetFolderNode, DatasetFolderTree } from "@/api/datasetFolder";
import { canModifyDatasetFolder, type UserRole } from "@/app";
import { hatcheryTextModes } from "@/app/theme";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  FolderIcon,
  PlusIcon,
  RefreshIcon,
} from "@/components/icons";
import { HatTextTooltip } from "@/components/tooltip";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import { resolveLoadErrorMessage, type LoadState } from "../loadState";
import { TreeRow } from "../TreeRow";
import {
  allFolderLabel,
  allFolderSelection,
  ancestorFolderUuids,
  expandableFolderUuids,
  folderMonogram,
  folderSelectionLabel,
  isSameFolderSelection,
  totalDatasetCount,
  unfiledFolderLabel,
  type FolderSelection,
} from "./folderTree";
import styles from "./DatasetFolderLane.module.css";

// 접힘 상태 기억. 앱 사이드바(`hatchery-registry:sidebar-collapsed`)와 같은 종류의 키다.
const COLLAPSED_STORAGE_KEY = "hatchery-registry:dataset-folder-lane-collapsed";

function readStoredCollapsed(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    return window.localStorage.getItem(COLLAPSED_STORAGE_KEY) === "true";
  } catch {
    // private 모드·사이트 데이터 차단에서는 접근이 예외를 던진다. 기억은 편의 기능이므로 펼친 상태로 연다.
    return false;
  }
}

function writeStoredCollapsed(collapsed: boolean): void {
  try {
    window.localStorage.setItem(COLLAPSED_STORAGE_KEY, String(collapsed));
  } catch {
    // 기억하지 못해도 이번 세션 동작은 그대로다.
  }
}

export type DatasetFolderLaneProps = {
  state: LoadState<DatasetFolderTree>;
  selection: FolderSelection;
  onSelect: (selection: FolderSelection) => void;
  currentUserRole: UserRole | null;
  // 폴더 쓰기(생성·이름 변경·이동·삭제) 요청. 페이지가 다이얼로그를 소유한다.
  onCreate: (parentUuid: string | null) => void;
  onRename: (node: DatasetFolderNode) => void;
  onMove: (node: DatasetFolderNode) => void;
  onDelete: (node: DatasetFolderNode) => void;
  // 트리 조회 실패에서 다시 받는다.
  onRetry: () => void;
  // 건수를 숨긴다. 트리의 건수가 옆 목록의 게이트(저장 위치)와 다른 모집단의 것인 동안 true 다.
  // 저장 위치를 바꾼 직후 새 트리를 받기 전이 그 경우다. 모집단이 다른 수는 표시하지 않는다
  // (docs/rules.md §17).
  countsHidden?: boolean;
};

/**
 * 데이터셋 목록 좌측 폴더 트리 레인.
 *
 * 고정 행 셋(`전체 데이터셋` · 루트 폴더들 · `미분류`)으로 트리를 구성한다. 선택은 단일이고,
 * `전체 데이터셋` 을 고르면 선택이 해제된다. 이 선택은 필터 바의 적용 조건 칩으로 표시하지 않는다.
 * 폴더는 매칭 방식(`filter_match`)을 따르지 않는 고정 게이트다. 매칭 방식을 따르는 조건만 모으는
 * 칩 레일에 넣으면 뜻이 맞지 않는다.
 *
 * 각 노드의 건수는 자손을 포함하므로 그 노드를 고른 목록의 `total` 과 같다. 두 수가 같아야
 * 트리가 "왜 빠졌나" 를 설명하는 표가 된다.
 *
 * 소유: 트리 렌더·펼침 상태·접힘 기억·선택 방출.
 * 비소유: 트리 데이터 로드(호출부의 `useDatasetFolderTree`), 쓰기 다이얼로그(페이지).
 */
export function DatasetFolderLane({
  state,
  selection,
  onSelect,
  currentUserRole,
  onCreate,
  onRename,
  onMove,
  onDelete,
  onRetry,
  countsHidden = false,
}: DatasetFolderLaneProps): ReactNode {
  const [collapsed, setCollapsed] = useState(readStoredCollapsed);
  const [expandedUuids, setExpandedUuids] = useState<ReadonlySet<string>>(new Set());
  const previousExpandableUuidsRef = useRef<ReadonlySet<string>>(new Set());
  const canEdit = canModifyDatasetFolder(currentUserRole);

  // 첫 조회는 모두 펼치고, 재조회는 사용자가 접은 상태를 보존한다. 첫 자식이 생긴 부모와
  // 선택된 자식의 부모는 펼친다. 그래서 새 항목이나 선택된 행이 접힌 폴더 안에 숨지 않는다.
  useEffect(() => {
    if (state.status !== "success") {
      return;
    }
    const expandable = expandableFolderUuids(state.data);
    const toExpand = new Set(ancestorFolderUuids(state.data, selection));
    for (const uuid of expandable) {
      if (!previousExpandableUuidsRef.current.has(uuid)) {
        toExpand.add(uuid);
      }
    }
    previousExpandableUuidsRef.current = expandable;
    if (toExpand.size === 0) {
      return;
    }
    setExpandedUuids((current) => {
      const missing = [...toExpand].filter((uuid) => !current.has(uuid));
      return missing.length === 0 ? current : new Set([...current, ...missing]);
    });
  }, [state, selection]);

  const toggleCollapsed = (): void => {
    setCollapsed((current) => {
      writeStoredCollapsed(!current);
      return !current;
    });
  };

  if (collapsed) {
    return (
      <div className={styles.laneCollapsed}>
        {/* 접힌 동안 트리는 렌더되지 않으므로 aria-controls 로 가리킬 대상이 없다. */}
        <IconButton
          size="sm"
          variant="surface"
          aria-label="폴더 레인 펼치기"
          aria-expanded={false}
          onClick={toggleCollapsed}
        >
          <ChevronRightIcon />
        </IconButton>
        <CollapsedSelectionChip state={state} selection={selection} onExpand={toggleCollapsed} />
      </div>
    );
  }

  return (
    <div className={styles.lane} id="dataset-folder-lane">
      <div className={styles.header}>
        <Text variant={hatcheryTextModes["caption-bold"]} className={styles.headerTitle}>
          폴더
        </Text>
        {canEdit ? (
          <IconButton
            size="sm"
            variant="surface"
            aria-label="새 폴더"
            onClick={() => onCreate(null)}
          >
            <PlusIcon />
          </IconButton>
        ) : null}
        <IconButton
          size="sm"
          variant="surface"
          aria-label="폴더 레인 접기"
          aria-expanded
          aria-controls="dataset-folder-lane"
          onClick={toggleCollapsed}
        >
          <ChevronLeftIcon />
        </IconButton>
      </div>

      <Scrollbar className={styles.tree} aria-label="폴더">
        <div className={styles.treeInner}>
          <LaneBody
            state={state}
            selection={selection}
            onSelect={onSelect}
            canEdit={canEdit}
            expandedUuids={expandedUuids}
            setExpandedUuids={setExpandedUuids}
            onCreate={onCreate}
            onRename={onRename}
            onMove={onMove}
            onDelete={onDelete}
            onRetry={onRetry}
            countsHidden={countsHidden}
          />
        </div>
      </Scrollbar>
    </div>
  );
}

function LaneBody({
  state,
  selection,
  onSelect,
  canEdit,
  expandedUuids,
  setExpandedUuids,
  onCreate,
  onRename,
  onMove,
  onDelete,
  onRetry,
  countsHidden,
}: {
  state: LoadState<DatasetFolderTree>;
  selection: FolderSelection;
  onSelect: (selection: FolderSelection) => void;
  canEdit: boolean;
  expandedUuids: ReadonlySet<string>;
  setExpandedUuids: (update: (current: ReadonlySet<string>) => ReadonlySet<string>) => void;
  onCreate: (parentUuid: string | null) => void;
  onRename: (node: DatasetFolderNode) => void;
  onMove: (node: DatasetFolderNode) => void;
  onDelete: (node: DatasetFolderNode) => void;
  onRetry: () => void;
  countsHidden: boolean;
}): ReactNode {
  if (state.status === "loading" || state.status === "idle") {
    return <LoadingState>폴더를 불러오고 있습니다.</LoadingState>;
  }
  if (state.status === "error") {
    return (
      <EmptyState
        tone="error"
        action={{ label: "다시 시도", onClick: onRetry, icon: <RefreshIcon /> }}
      >
        {resolveLoadErrorMessage(state.message)}
      </EmptyState>
    );
  }

  const tree = state.data;
  // 예약한 칸은 폴더 행과 줄을 맞추려고 있다. 맞출 대상이 없으면 빈 공간만 남는다.
  // 폴더가 하나도 없는 카탈로그에서는 이 칸이 좁은 레인의 1/5 을 빈 공간으로 차지한다.
  // 예약할 때는 액션이 없는 고정 행도 함께 예약해야 건수의 오른쪽 끝이 한 줄로 맞는다.
  const caretColumn = tree.folders.some((node) => node.children.length > 0);
  const reserveActions = canEdit && tree.folders.length > 0;

  return (
    <>
      <TreeRow
        mode="select"
        label={allFolderLabel}
        count={countsHidden ? null : totalDatasetCount(tree)}
        depth={0}
        icon={<FolderIcon />}
        selected={isSameFolderSelection(selection, allFolderSelection)}
        onSelect={() => onSelect(allFolderSelection)}
        caretColumn={caretColumn}
        revealTrailingOnHover={reserveActions}
      />
      {/* 해제 행과 묶음들을 구분한다. 아래는 모두 "어느 묶음" 이고, `전체` 만 해제다. */}
      <div className={styles.divider} aria-hidden="true" />
      {tree.folders.map((node) => (
        <FolderTreeNode
          key={node.uuid}
          node={node}
          depth={0}
          caretColumn={caretColumn}
          selection={selection}
          onSelect={onSelect}
          canEdit={canEdit}
          expandedUuids={expandedUuids}
          setExpandedUuids={setExpandedUuids}
          onCreate={onCreate}
          onRename={onRename}
          onMove={onMove}
          onDelete={onDelete}
          countsHidden={countsHidden}
        />
      ))}
      <TreeRow
        mode="select"
        label={unfiledFolderLabel}
        count={countsHidden ? null : tree.unfiled_count}
        depth={0}
        icon={<FolderIcon />}
        selected={isSameFolderSelection(selection, { kind: "unfiled" })}
        onSelect={() => onSelect({ kind: "unfiled" })}
        caretColumn={caretColumn}
        revealTrailingOnHover={reserveActions}
      />
    </>
  );
}

function FolderTreeNode({
  node,
  depth,
  caretColumn,
  selection,
  onSelect,
  canEdit,
  expandedUuids,
  setExpandedUuids,
  onCreate,
  onRename,
  onMove,
  onDelete,
  countsHidden,
}: {
  node: DatasetFolderNode;
  depth: number;
  // 트리 전체가 공유하는 캐럿 칸 여부. 행마다 다르면 이름 시작선이 어긋난다.
  caretColumn: boolean;
  selection: FolderSelection;
  onSelect: (selection: FolderSelection) => void;
  canEdit: boolean;
  expandedUuids: ReadonlySet<string>;
  setExpandedUuids: (update: (current: ReadonlySet<string>) => ReadonlySet<string>) => void;
  onCreate: (parentUuid: string | null) => void;
  onRename: (node: DatasetFolderNode) => void;
  onMove: (node: DatasetFolderNode) => void;
  onDelete: (node: DatasetFolderNode) => void;
  countsHidden: boolean;
}): ReactNode {
  const expanded = expandedUuids.has(node.uuid);
  const hasChildren = node.children.length > 0;

  const toggle = (): void =>
    setExpandedUuids((current) => {
      const next = new Set(current);
      if (next.has(node.uuid)) {
        next.delete(node.uuid);
      } else {
        next.add(node.uuid);
      }
      return next;
    });

  return (
    <>
      <TreeRow
        mode="select"
        label={node.display_name}
        count={countsHidden ? null : node.dataset_count}
        depth={depth}
        icon={<FolderIcon />}
        selected={isSameFolderSelection(selection, { kind: "folder", uuid: node.uuid })}
        onSelect={() => onSelect({ kind: "folder", uuid: node.uuid })}
        expanded={hasChildren ? expanded : undefined}
        onToggleExpanded={hasChildren ? toggle : undefined}
        caretColumn={caretColumn}
        // member 에게는 행 메뉴가 없으므로 예약할 칸도 없다.
        revealTrailingOnHover={canEdit}
        trailing={
          canEdit ? (
            <Menu label={`${node.display_name} 폴더 작업`}>
              <Menu.Trigger label="폴더 작업" />
              {/* 깊이 상한이 2 이므로 자식 아래에는 더 만들 수 없다. */}
              {depth === 0 ? (
                <Menu.Item onSelect={() => onCreate(node.uuid)}>하위 폴더 만들기</Menu.Item>
              ) : null}
              <Menu.Item onSelect={() => onRename(node)}>이름 변경</Menu.Item>
              <Menu.Item onSelect={() => onMove(node)}>옮기기</Menu.Item>
              <Menu.Item tone="danger" onSelect={() => onDelete(node)}>
                삭제
              </Menu.Item>
            </Menu>
          ) : null
        }
      />
      {expanded
        ? node.children.map((child) => (
            <FolderTreeNode
              key={child.uuid}
              node={child}
              depth={depth + 1}
              caretColumn={caretColumn}
              selection={selection}
              onSelect={onSelect}
              canEdit={canEdit}
              expandedUuids={expandedUuids}
              setExpandedUuids={setExpandedUuids}
              onCreate={onCreate}
              onRename={onRename}
              onMove={onMove}
              onDelete={onDelete}
              countsHidden={countsHidden}
            />
          ))
        : null}
    </>
  );
}

/**
 * 접힌 레인에 남는 현재 위치 표식. 첫 글자를 보이고, hover 하면 전체 경로를 보인다.
 *
 * 누르면 레인을 펼친다. 폴더를 고르는 버튼이 아니라 레인을 여는 버튼이므로 이름에 동작을 넣는다.
 *
 * 이름을 알 수 없으면 아무것도 그리지 않는다. 지워진 폴더를 가리키는 링크이거나 트리를 받지
 * 못한 상태에서 `?` 를 표시하면, 화면은 "고른 폴더가 있는데 이름을 모른다" 고 표시한다. 그러나
 * 펼쳐도 선택된 행이 없다. 그러면 두 표시가 서로 맞지 않는다.
 */
function CollapsedSelectionChip({
  state,
  selection,
  onExpand,
}: {
  state: LoadState<DatasetFolderTree>;
  selection: FolderSelection;
  onExpand: () => void;
}): ReactNode {
  const label = folderSelectionLabel(state.status === "success" ? state.data : null, selection);
  if (label === null) {
    return null;
  }

  return (
    <HatTextTooltip text={label.path} placement="right" mode="plain">
      <button
        type="button"
        className={styles.collapsedChip}
        aria-label={`${label.name} 폴더 레인 펼치기`}
        aria-current="true"
        aria-expanded={false}
        onClick={onExpand}
      >
        <span className={styles.collapsedGlyph} aria-hidden="true">
          {/* 전체는 해제 상태이므로 글자가 뜻에 맞지 않는다. 폴더 글리프를 그대로 쓴다. */}
          {selection.kind === "all" ? <FolderIcon /> : folderMonogram(label.name)}
        </span>
      </button>
    </HatTextTooltip>
  );
}
